import { createLogic } from 'redux-logic';

import {
  searchCustomersFulfilled,
  searchCustomersRejected,
} from 'store/searchCustomers/actions';
import { queryAccounts } from 'store/api/searchCustomer';
import {
  SEARCH_CUSTOMERS,
  AccountQueryName,
} from 'store/searchCustomers/types';
import { getFirstName, getLastName, isFullName, isValidName } from 'utils/name';

const ACCOUNT_QUERY_VALIDATOR = {
  [AccountQueryName.PHONE_NUMBER]: (searchValue) => {
    return searchValue.length === 10 && /^\d+$/.test(searchValue);
  },
  [AccountQueryName.EMAIL]: (searchValue) => {
    // Simple regex to test {anystring}@{anystring}.{anystring}
    var reg = /\S+@\S+\.\S+/;
    return reg.test(searchValue);
  },
  [AccountQueryName.CARD_NUMBER]: (searchValue) => {
    return (
      searchValue.length >= 2 &&
      searchValue.length <= 76 &&
      searchValue.length !== 10 &&
      /^\d+$/.test(searchValue)
    );
  },
  [AccountQueryName.FIRST_NAME]: (searchValue) => {
    return (
      isValidName(searchValue) &&
      !isFullName(searchValue) &&
      // Paytronix API requires minimum 2 characters.
      searchValue.length > 1
    );
  },
  [AccountQueryName.LAST_NAME]: (searchValue) => {
    return (
      isValidName(searchValue) &&
      !isFullName(searchValue) &&
      // Paytronix API requires minimum 2 characters.
      searchValue.length > 1
    );
  },
  [AccountQueryName.FULL_NAME]: (searchValue) => {
    return (
      isValidName(searchValue) &&
      isFullName(searchValue) &&
      // Paytronix API requires minimum 2 characters.
      getFirstName(searchValue).length > 1 &&
      getLastName(searchValue).length > 1
    );
  },
};

function getValidAccountQueriesList(searchValue, allowedAccountQueryNames) {
  return (
    Object.values(AccountQueryName)
      // Filter any account query names that are not valid.
      .filter(
        (accountQueryName) =>
          allowedAccountQueryNames.includes(accountQueryName) &&
          ACCOUNT_QUERY_VALIDATOR[accountQueryName](searchValue)
      )
      // Map valid account query name to account query.
      .map((accountQueryName) => {
        if (accountQueryName === AccountQueryName.FULL_NAME) {
          return [
            {
              name: AccountQueryName.FIRST_NAME,
              value: getFirstName(searchValue),
            },
            {
              name: AccountQueryName.LAST_NAME,
              value: getLastName(searchValue),
            },
          ];
        }

        return [{ name: accountQueryName, value: searchValue }];
      })
  );
}

export const searchCustomersLogic = createLogic({
  type: SEARCH_CUSTOMERS,
  latest: true,

  async process({ action, getState }, dispatch, done) {
    const searchValue = action.payload.value;
    const options = action.payload.options;

    // Build list of allowed account query names from options.
    const allowedAccountQueryNames = [
      options.allowEmailSearch ? AccountQueryName.EMAIL : null,
      options.allowPhoneNumSearch ? AccountQueryName.PHONE_NUMBER : null,
      options.allowCardNumSearch ? AccountQueryName.CARD_NUMBER : null,
      options.allowNameSearch ? AccountQueryName.FIRST_NAME : null,
      options.allowNameSearch ? AccountQueryName.LAST_NAME : null,
      options.allowNameSearch ? AccountQueryName.FULL_NAME : null,
    ].filter((accountQueryNameOrNull) => accountQueryNameOrNull !== null);

    const validAccountQueriesList = getValidAccountQueriesList(
      searchValue,
      allowedAccountQueryNames
    );

    if (validAccountQueriesList.length === 0) {
      dispatch(
        searchCustomersRejected({
          validationError: new Error('Invalid search value.'),
        })
      );
      done();
      return;
    }

    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;

    try {
      const responses = await Promise.all(
        validAccountQueriesList.map((validAccountQueries) =>
          queryAccounts(posContext, pxContext, validAccountQueries)
        )
      );
      const customers = responses
        .map((response) => response.customers)
        .flat()
        .sort((customer1, customer2) => {
          const givenName1 = customer1?.givenName ?? '';
          const familyName1 = customer1?.familyName ?? '';
          const givenName2 = customer2?.givenName ?? '';
          const familyName2 = customer2?.familyName ?? '';

          if (givenName1 === givenName2) {
            return familyName1.localeCompare(familyName2, undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          }

          return givenName1.localeCompare(givenName2, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        });

      dispatch(searchCustomersFulfilled(customers));
    } catch (error) {
      dispatch(searchCustomersRejected({ apiError: error }));
    } finally {
      done();
    }
  },
});
